import { type ComponentProps, forwardRef } from 'react';

import { AspectRatio, type AspectRatioProps, chakra } from '@chakra-ui/react';

import { useForwardedRef } from '@/utils/hooks';

import { useUpdateVideoSrc } from './useUpdateVideoSrc';

type KnownVideoPropsKeys =
  | 'autoPlay'
  | 'controls'
  | 'loop'
  | 'muted'
  | 'playsInline';

type VideoHTMLProps = ComponentProps<typeof chakra.video>;

type KnownVideoProps = Pick<VideoHTMLProps, KnownVideoPropsKeys>;

type VideoPlayerProps = AspectRatioProps &
  KnownVideoProps & {
    videoProps?: Omit<VideoHTMLProps, KnownVideoPropsKeys>;
  };

export const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (props, ref) => {
    const {
      autoPlay,
      controls = true,
      loop,
      muted,
      playsInline,
      videoProps,
      ...rest
    } = props;

    const videoRef = useForwardedRef(ref);

    useUpdateVideoSrc(videoRef, videoProps?.src);

    return (
      <AspectRatio
        ratio={3 / 2}
        pos="relative"
        overflow="hidden"
        bg="gray.700"
        rounded="xl"
        w="full"
        h="full"
        minH="350px"
        {...rest}
      >
        <chakra.video
          ref={videoRef}
          w="full"
          pos="absolute"
          controls={controls}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload"
          {...videoProps}
        />
      </AspectRatio>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';
